import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { AdminUserDto, AdminUserListResponse, FakeUserDto, FakeUserListResponse } from '@upme/contracts';
import { api } from '../../app/api-client';
import type { UsersSearch } from '../../routes/_authenticated/users/route';

export const usersKey = ['admin', 'users'] as const;

/** Пустые значения не отправляем: бэкенд сам подставит дефолты реестра. */
function toSearchParams(filters: UsersSearch): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    out[key] = String(value);
  }
  return out;
}

export const listUsersOptions = (filters: UsersSearch) =>
  queryOptions({
    queryKey: [...usersKey, 'list', filters] as const,
    queryFn: () =>
      api.get('admin/users', { searchParams: toSearchParams(filters) }).json<AdminUserListResponse>(),
    staleTime: 10_000,
  });

export const searchByUsernameOptions = (username: string) =>
  queryOptions({
    queryKey: [...usersKey, 'search', username] as const,
    queryFn: () => api.get('admin/users/search', { searchParams: { username } }).json<AdminUserDto[]>(),
    enabled: username.length > 0,
    staleTime: 0,
  });

export const userByIdOptions = (id: string) =>
  queryOptions({
    queryKey: [...usersKey, id] as const,
    queryFn: () => api.get(`admin/users/${id}`).json<AdminUserDto>(),
  });

export const fakesKey = [...usersKey, 'fakes'] as const;

export interface FakeListFilters {
  cityId?: string;
  discoverable?: boolean;
  limit?: number;
}

export const listFakesInfiniteOptions = (filters: FakeListFilters = {}) =>
  infiniteQueryOptions({
    queryKey: [...fakesKey, 'list-infinite', filters] as const,
    queryFn: ({ pageParam }) => {
      const searchParams: Record<string, string> = {};
      if (filters.cityId) searchParams.cityId = filters.cityId;
      if (filters.discoverable !== undefined) searchParams.discoverable = String(filters.discoverable);
      if (filters.limit) searchParams.limit = String(filters.limit);
      if (pageParam) searchParams.cursor = pageParam;
      return api.get('admin/fake-users', { searchParams }).json<FakeUserListResponse>();
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 5_000,
  });

export const fakeByIdOptions = (id: string) =>
  queryOptions({
    queryKey: [...fakesKey, id] as const,
    queryFn: () => api.get(`admin/fake-users/${id}`).json<FakeUserDto>(),
  });

/** Все CITY_MODERATOR одним запросом (их единицы) — для выбора менеджера события (#166). */
export const cityModeratorsOptions = () =>
  queryOptions({
    queryKey: [...usersKey, 'city-moderators'] as const,
    queryFn: () =>
      api
        .get('admin/users', { searchParams: { adminRole: 'CITY_MODERATOR', limit: '100' } })
        .json<AdminUserListResponse>(),
    staleTime: 30_000,
  });
